import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ScanSearch,
  ShieldCheck,
  Tag,
  Megaphone,
  Users,
  Award,
  Percent,
  Network,
} from 'lucide-react';
import { useState } from 'react';

import type { MlmCommissionRule, RankTier, ReferralCampaign, Team } from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const pct = (bps: number) => `${(bps / 100).toFixed(2)}%`;

function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-border/60 bg-surface-subtle/30 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border p-2 shadow-sm">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`flex-1 rounded-xl px-4 py-2.5 text-[13px] font-bold transition-all sm:flex-none ${
            active === t
              ? 'bg-surface text-ink border-border/60 border shadow-sm'
              : 'text-ink-muted hover:bg-surface-subtle/50 hover:text-ink border border-transparent'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// AI  (Phase 3.2 — engines: pricing scan, fraud scan, content tagging)
// ══════════════════════════════════════════════════════════════════

function JobHistory() {
  const qc = useQueryClient();
  const jobs = useQuery({
    queryKey: ['ai-jobs'],
    queryFn: () => api.ai.listJobs(),
    refetchInterval: 3000,
  });

  const rollback = useMutation({
    mutationFn: (jobId: string) => api.ai.rollbackJob(jobId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-jobs'] }),
  });

  return (
    <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm mt-6">
      <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
        <Network className="h-4 w-4" /> Scan History & Jobs
      </h3>
      
      {jobs.isLoading ? (
        <div className="py-8 text-center"><Spinner /></div>
      ) : (jobs.data ?? []).length === 0 ? (
        <div className="py-8"><EmptyState icon={<Network />} title="No jobs found" description="Run a scan to see history." /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Result / Rollback</th>
              </tr>
            </thead>
            <tbody className="divide-border/40 divide-y text-[13px]">
              {(jobs.data ?? []).map((j: any) => (
                <tr key={j.id}>
                  <td className="px-4 py-3 font-semibold">{j.kind}</td>
                  <td className="px-4 py-3">
                    <Badge tone={j.status === 'COMPLETED' ? 'success' : j.status === 'FAILED' ? 'danger' : 'neutral'}>
                      {j.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-subtle">
                    {j.status === 'RUNNING' ? `${j.progress} / ${j.totalItems}` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {j.status === 'COMPLETED' && (
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-ink-subtle">{JSON.stringify(j.result)}</span>
                        <Button size="sm" variant="ghost" onClick={() => rollback.mutate(j.id)} loading={rollback.isPending}>
                          Rollback
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AiPage() {
  const qc = useQueryClient();
  const pricingScan = useMutation({ 
    mutationFn: () => api.ai.runPricingScan({ limit: 500 }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-jobs'] }),
  });
  const fraudScan = useMutation({ 
    mutationFn: () => api.ai.runFraudScan({ limit: 500 }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-jobs'] }),
  });
  const [productId, setProductId] = useState('');
  const tag = useMutation({
    mutationFn: () => api.ai.tagProduct({ productId: productId.trim(), persist: false }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-jobs'] }),
  });

  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">AI Engines</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Run the dynamic pricing, fraud detection and content-tagging engines.
          </p>
        </div>
      </div>

      {(pricingScan.error || fraudScan.error || tag.error) && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl text-sm font-bold">
           Feature might be disabled or quota exceeded. Ensure AI_FEATURES_ENABLED=true in the API environment.
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm transition-all hover:shadow-md">
          <h3 className="text-ink-muted mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
            <ScanSearch className="h-4 w-4" /> Pricing Scan
          </h3>
          <p className="text-ink-subtle mb-6 text-[13px] font-medium">
            Detects stock pressure, demand spikes and clearance candidates; proposes pricing
            signals.
          </p>
          <Button
            onClick={() => pricingScan.mutate()}
            loading={pricingScan.isPending}
            className="px-6"
          >
            Start Pricing Scan
          </Button>
        </div>

        <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm transition-all hover:shadow-md">
          <h3 className="text-ink-muted mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
            <ShieldCheck className="h-4 w-4" /> Fraud Scan
          </h3>
          <p className="text-ink-subtle mb-6 text-[13px] font-medium">
            Runs refund-velocity, withdrawal-anomaly and other detectors; flags fraud signals.
          </p>
          <Button onClick={() => fraudScan.mutate()} loading={fraudScan.isPending} className="px-6">
            Start Fraud Scan
          </Button>
        </div>
      </div>

      <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Tag className="h-4 w-4" /> Content Tagging
        </h3>
        <form
          className="flex flex-col gap-4 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (productId.trim()) tag.mutate();
          }}
        >
          <div className="flex-1">
            <Input
              label="Product ID"
              placeholder="Enter product ID to tag"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="font-mono text-[13px]"
            />
          </div>
          <Button type="submit" loading={tag.isPending} className="h-10 px-8">
            Tag Product
          </Button>
        </form>
      </div>

      <JobHistory />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NETWORK  (Phase 3.3 — campaigns, teams, ranks, MLM rules)
// ══════════════════════════════════════════════════════════════════

const CAMPAIGN_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ENDED: 'neutral',
};

function CampaignsTab() {
  const qc = useQueryClient();
  const campaigns = useQuery({
    queryKey: ['net-campaigns'],
    queryFn: () => api.network.listCampaigns(),
  });
  const [form, setForm] = useState({ code: '', name: '', referrerRupees: '', refereeRupees: '' });

  const upsert = useMutation({
    mutationFn: () =>
      api.network.upsertCampaign({
        code: form.code.toUpperCase(),
        name: form.name.trim(),
        referrerRewardPaise: Math.round(Number(form.referrerRupees) * 100),
        refereeRewardPaise: Math.round(Number(form.refereeRupees) * 100),
      }),
    onSuccess: () => {
      setForm({ code: '', name: '', referrerRupees: '', refereeRupees: '' });
      void qc.invalidateQueries({ queryKey: ['net-campaigns'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Megaphone className="h-4 w-4" /> Upsert Campaign
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.code && form.name) upsert.mutate();
          }}
        >
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            required
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Referrer Reward (₹)"
            type="number"
            min="0"
            step="0.01"
            value={form.referrerRupees}
            onChange={(e) => setForm((f) => ({ ...f, referrerRupees: e.target.value }))}
            required
          />
          <Input
            label="Referee Reward (₹)"
            type="number"
            min="0"
            step="0.01"
            value={form.refereeRupees}
            onChange={(e) => setForm((f) => ({ ...f, refereeRupees: e.target.value }))}
            required
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-4">
            <Button type="submit" loading={upsert.isPending} className="px-8">
              Save Campaign
            </Button>
          </div>
        </form>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {campaigns.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (campaigns.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Megaphone className="text-ink-muted h-10 w-10" />}
              title="No campaigns"
              description="Create your first referral campaign above."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-right">Referrer</th>
                  <th className="px-4 py-3 text-right">Referee</th>
                  <th className="px-4 py-3 text-center">Priority</th>
                  <th className="px-4 py-3 pr-5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(campaigns.data ?? []).map((c: ReferralCampaign) => (
                  <tr key={c.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">{c.code}</td>
                    <td className="px-4 py-3 font-semibold">{c.name}</td>
                    <td className="text-ink px-4 py-3 text-right font-semibold">
                      {formatPaise(c.referrerRewardPaise)}
                    </td>
                    <td className="text-ink px-4 py-3 text-right font-semibold">
                      {formatPaise(c.refereeRewardPaise)}
                    </td>
                    <td className="text-ink px-4 py-3 text-center font-bold">{c.priority}</td>
                    <td className="px-4 py-3 pr-5 text-center">
                      <Badge tone={CAMPAIGN_TONE[c.status] ?? 'neutral'}>{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamsTab() {
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['net-teams'], queryFn: () => api.network.listTeams() });
  const [form, setForm] = useState({ code: '', name: '', leaderUserId: '' });

  const create = useMutation({
    mutationFn: () =>
      api.network.createTeam({
        code: form.code.toUpperCase(),
        name: form.name.trim(),
        leaderUserId: form.leaderUserId.trim(),
      }),
    onSuccess: () => {
      setForm({ code: '', name: '', leaderUserId: '' });
      void qc.invalidateQueries({ queryKey: ['net-teams'] });
    },
  });
  const refresh = useMutation({
    mutationFn: (id: string) => api.network.refreshTeamGmv(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['net-teams'] }),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Users className="h-4 w-4" /> New Team
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.code && form.name && form.leaderUserId) create.mutate();
          }}
        >
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            required
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Leader User ID"
            value={form.leaderUserId}
            onChange={(e) => setForm((f) => ({ ...f, leaderUserId: e.target.value }))}
            required
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-3">
            <Button type="submit" loading={create.isPending} className="px-8">
              Create Team
            </Button>
          </div>
        </form>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {teams.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (teams.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Users className="text-ink-muted h-10 w-10" />}
              title="No teams"
              description="Create your first MLM team above."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-center">Members</th>
                  <th className="px-4 py-3 text-right">GMV Lifetime</th>
                  <th className="px-4 py-3 text-right">GMV 30d</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(teams.data ?? []).map((t: Team) => (
                  <tr key={t.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">{t.code}</td>
                    <td className="px-4 py-3 font-semibold">{t.name}</td>
                    <td className="text-ink px-4 py-3 text-center font-bold">
                      {t.activeMemberCount}
                    </td>
                    <td className="text-ink px-4 py-3 text-right font-semibold">
                      {formatPaise(t.gmvLifetimePaise)}
                    </td>
                    <td className="text-ink px-4 py-3 text-right font-semibold">
                      {formatPaise(t.gmv30dPaise)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone={t.status === 'ACTIVE' ? 'success' : 'neutral'}>{t.status}</Badge>
                    </td>
                    <td className="px-4 py-3 pr-5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => refresh.mutate(t.id)}>
                        Refresh GMV
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RanksTab() {
  const qc = useQueryClient();
  const tiers = useQuery({ queryKey: ['net-tiers'], queryFn: () => api.network.listRankTiers() });
  const runEngine = useMutation({
    mutationFn: () => api.network.runRankEngine(1000),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['net-tiers'] }),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <Button onClick={() => runEngine.mutate()} loading={runEngine.isPending} className="px-6">
          Run Rank Engine
        </Button>
        {runEngine.data && (
          <div className="rounded-lg bg-emerald-500/10 px-4 py-2 text-[13px] font-bold text-emerald-600">
            Scanned {runEngine.data.scanned} · Promoted {runEngine.data.promotedCount} · Demoted{' '}
            {runEngine.data.demotedCount}
          </div>
        )}
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {tiers.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (tiers.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Award className="text-ink-muted h-10 w-10" />}
              title="No rank tiers"
              description="Rank tiers will appear here."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5 text-center">Ordinal</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3 text-right">Min GMV Lifetime</th>
                  <th className="px-4 py-3 text-center">Min Direct Referrals</th>
                  <th className="px-4 py-3 text-right">Commission Multiplier</th>
                  <th className="px-4 py-3 pr-5 text-center">Active</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(tiers.data ?? []).map((t: RankTier) => (
                  <tr key={t.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="text-ink px-4 py-3 pl-5 text-center font-bold">{t.ordinal}</td>
                    <td className="px-4 py-3">
                      <Badge tone="neutral">{t.code}</Badge>
                    </td>
                    <td className="text-ink px-4 py-3 text-right font-semibold">
                      {formatPaise(t.minGmvLifetimePaise)}
                    </td>
                    <td className="text-ink px-4 py-3 text-center font-bold">
                      {t.minDirectReferrals}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-600">
                      {(t.commissionMultiplierBps / 10000).toFixed(2)}×
                    </td>
                    <td className="px-4 py-3 pr-5 text-center">
                      {t.active ? (
                        <Badge tone="success">Yes</Badge>
                      ) : (
                        <Badge tone="neutral">No</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MlmRulesTab() {
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: ['net-mlm'], queryFn: () => api.network.listMlmRules() });
  const [form, setForm] = useState({ level: '1', ratePct: '' });

  const upsert = useMutation({
    mutationFn: () =>
      api.network.upsertMlmRule({
        level: Number(form.level),
        rateBps: Math.round(Number(form.ratePct) * 100),
      }),
    onSuccess: () => {
      setForm({ level: '1', ratePct: '' });
      void qc.invalidateQueries({ queryKey: ['net-mlm'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Network className="h-4 w-4" /> Upsert MLM Commission Rule
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.ratePct) upsert.mutate();
          }}
        >
          <Input
            label="Level"
            type="number"
            min="1"
            max="20"
            value={form.level}
            onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
            required
          />
          <Input
            label="Rate (%)"
            type="number"
            min="0"
            max="50"
            step="0.01"
            value={form.ratePct}
            onChange={(e) => setForm((f) => ({ ...f, ratePct: e.target.value }))}
            required
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-3">
            <Button type="submit" loading={upsert.isPending} className="px-8">
              Save Rule
            </Button>
          </div>
        </form>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {rules.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (rules.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Network className="text-ink-muted h-10 w-10" />}
              title="No MLM rules"
              description="Define commission rates per downline level above."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5 text-center">Level</th>
                  <th className="px-4 py-3 text-right">Commission Rate</th>
                  <th className="px-4 py-3 pr-5 text-center">Active</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(rules.data ?? [])
                  .slice()
                  .sort((a: MlmCommissionRule, b: MlmCommissionRule) => a.level - b.level)
                  .map((r: MlmCommissionRule) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 group transition-colors">
                      <td className="text-ink px-4 py-3 pl-5 text-center font-bold">L{r.level}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                        {pct(r.rateBps)}
                      </td>
                      <td className="px-4 py-3 pr-5 text-center">
                        {r.active ? (
                          <Badge tone="success">Yes</Badge>
                        ) : (
                          <Badge tone="neutral">No</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function NetworkPage() {
  const [tab, setTab] = useState('Campaigns');

  if (import.meta.env.VITE_FEATURE_MLM_ENABLED !== 'true') {
    return (
      <div className="space-y-6 pb-8">
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl text-sm font-bold">
           Network/MLM features are disabled due to compliance/risk policies. Enable VITE_FEATURE_MLM_ENABLED in your environment to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Network & MLM</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Referral campaigns, teams, rank tiers and multi-level commission rules.
          </p>
        </div>
      </div>
      <Tabs tabs={['Campaigns', 'Teams', 'Ranks', 'MLM Rules']} active={tab} onChange={setTab} />
      {tab === 'Campaigns' && <CampaignsTab />}
      {tab === 'Teams' && <TeamsTab />}
      {tab === 'Ranks' && <RanksTab />}
      {tab === 'MLM Rules' && <MlmRulesTab />}
    </div>
  );
}
